type SendApplicationMessageFn = (payload: Uint8Array) => boolean;
type ErrorReporterFn = (message: string) => void;

const HANDSHAKE_TIMEOUT_MS = 15000;
const POLLING_INTERVAL_MS = 400;
const MESSAGE_TYPE_DATA_CHANNELS_READY = 'dataChannelsReady';

export class DataChannelHandshakeService {
    private readonly onSecurityError: ErrorReporterFn;
    private readonly encoder: TextEncoder;
    private appReadyReceived: boolean;
    private appReadyPollingIntervalId: number | null;
    private handshakeTimeoutId: number | null;

    constructor(args: { onSecurityError: ErrorReporterFn }) {
        this.onSecurityError = args.onSecurityError;
        this.encoder = new TextEncoder();
        this.appReadyReceived = false;
        this.appReadyPollingIntervalId = null;
        this.handshakeTimeoutId = null;
    }

    public start(sendApplicationMessage: SendApplicationMessageFn): void {
        this.stop();
        this.appReadyReceived = false;
        this.pollUntilAppIsReady(sendApplicationMessage);
        this.setHandshakeTimeout();
    }

    public stop(): void {
        if (this.appReadyPollingIntervalId !== null) {
            window.clearInterval(this.appReadyPollingIntervalId);
            this.appReadyPollingIntervalId = null;
        }

        if (this.handshakeTimeoutId !== null) {
            window.clearTimeout(this.handshakeTimeoutId);
            this.handshakeTimeoutId = null;
        }
    }

    public onApplicationMessage(message: Uint8Array): void {
        if (this.appReadyReceived) {
            return;
        }

        const parsed = this.parseApplicationPayload(message);
        if (!parsed) {
            return;
        }

        if (parsed.type !== 'appReady') {
            return;
        }

        this.appReadyReceived = true;
        this.stop();
    }

    private setHandshakeTimeout(): void {
        this.handshakeTimeoutId = window.setTimeout(() => {
            if (this.appReadyReceived) {
                return;
            }

            this.stop();
            this.onSecurityError('Data channel handshake timeout: appReady not received within 15s');
        }, HANDSHAKE_TIMEOUT_MS);
    }

    private pollUntilAppIsReady(sendApplicationMessage: SendApplicationMessageFn): void {
        this.appReadyPollingIntervalId = window.setInterval(() => {
            if (this.appReadyReceived) {
                this.stop();
                return;
            }

            this.sendDataChannelReadyMessage(sendApplicationMessage);
        }, POLLING_INTERVAL_MS);
    }

    private sendDataChannelReadyMessage(sendApplicationMessage: SendApplicationMessageFn): void {
        const message = {
            type: MESSAGE_TYPE_DATA_CHANNELS_READY
        };
        const encodedMessage = this.encoder.encode(JSON.stringify(message));
        sendApplicationMessage(encodedMessage);
    }

    private parseApplicationPayload(message: Uint8Array): { type?: unknown } | null {
        let decoded = '';
        try {
            const decoder = new TextDecoder('utf-8', { fatal: false });
            decoded = decoder.decode(message);
        } catch (_error) {
            return null;
        }

        if (!decoded.trim()) {
            return null;
        }

        try {
            const parsed = JSON.parse(decoded) as Record<string, unknown>;
            return {
                type: parsed.type
            };
        } catch (_error) {
            return null;
        }
    }
}
